import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDeploymentDetail } from '@/features/deployments/hooks/useDeployments';
import { useParameters, useLayers, useParameterLive, useParameterStats } from '@/features/deploymentSettings/hooks/useDeploymentSettings';
import { useDeploymentRole } from '@/app/providers/DeploymentRoleContext';
import { SkeletonCard } from '@/shared/ui/SkeletonCard';
import { ErrorState } from '@/shared/ui/ErrorState';
import { AppTable } from '@/shared/ui/AppTable';
import { PageHeader } from '@/shared/ui/PageHeader';
import { ParameterChartsModal } from '@/features/deploymentSettings/ui/ParameterChartsModal';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { semantic, typography, radius } from '@/shared/theme/tokens';
import { PARAM_STATUS_META, PARAM_STATUS_FALLBACK_OK, PARAM_STATUS_FALLBACK_ALERT } from '@/shared/ui/paramStatusMeta';
import { lastNDaysRange } from '@/shared/lib/dateRange';
import { SearchInput } from '@/shared/ui/SearchInput';

function isInOptimal(value, param) {
  const lo = param.optimumLow != null && param.optimumLow !== '' ? Number(param.optimumLow) : null;
  const hi = param.optimumHigh != null && param.optimumHigh !== '' ? Number(param.optimumHigh) : null;
  if (lo === null && hi === null) return true;
  if (lo !== null && hi !== null) return value >= lo && value <= hi;
  if (lo !== null) return value >= lo;
  return value <= hi;
}

function formatOptimal(param) {
  const lo = param.optimumLow != null && param.optimumLow !== '' ? Number(param.optimumLow) : null;
  const hi = param.optimumHigh != null && param.optimumHigh !== '' ? Number(param.optimumHigh) : null;
  if (lo !== null && hi !== null) return `${lo} – ${hi}`;
  if (lo !== null) return `≥ ${lo}`;
  if (hi !== null) return `≤ ${hi}`;
  return '—';
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function timeAgo(isoString) {
  if (!isoString) return null;
  const diffMs = Date.now() - new Date(isoString).getTime();
  if (diffMs < 0) return 'just now';
  const totalSecs = Math.floor(diffMs / 1000);
  if (totalSecs < 60) return `${totalSecs}s ago`;
  const totalMins = Math.floor(totalSecs / 60);
  if (totalMins < 60) return `${totalMins} min ago`;
  const totalHrs = Math.floor(totalMins / 60);
  if (totalHrs < 24) return `${totalHrs} hr${totalHrs !== 1 ? 's' : ''} ago`;
  const days = Math.floor(totalHrs / 24);
  const remHrs = totalHrs % 24;
  if (remHrs === 0) return `${days} day${days !== 1 ? 's' : ''} ago`;
  return `${days} day${days !== 1 ? 's' : ''} ${remHrs} hr${remHrs !== 1 ? 's' : ''} ago`;
}

function formatTimestamp(isoString) {
  if (!isoString) return null;
  const d = new Date(isoString);
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function rangeToISO(days) {
  return lastNDaysRange(days);
}

// ─── Date range options ───────────────────────────────────────────────────────
const DATE_RANGES = [
  { label: '1D', days: 1 },
  { label: '7D', days: 7 },
  { label: '14D', days: 14 },
  { label: '30D', days: 30 },
];

// ─── Main page ────────────────────────────────────────────────────────────────
export default function DeploymentDashboardPage() {
  const { id } = useParams();
  const { data: deployment, isLoading: depLoading, isError: depError, error: depErr } = useDeploymentDetail(id);
  const { data: layers = [], isLoading: layersLoading, isError: layersError } = useLayers(id);
  const { data: parameters = [], isLoading: paramsLoading, isError: paramsError } = useParameters(id);
  const { setActiveDeployment } = useDeploymentRole();
  const c = useThemeColors();
  const isDark = useIsDark();

  const [searchParams, setSearchParams] = useSearchParams();
  const [rangeDays, setRangeDays] = useState(7);
  const [paramSearch, setParamSearch] = useState('');
  const [chartParam, setChartParam] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const chartParamIdParam = searchParams.get('param');

  function openParam(param) {
    setChartParam(param);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('param', param.id);
      return p;
    }, { replace: true });
  }

  function closeParam() {
    setChartParam(null);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('param');
      return p;
    }, { replace: true });
  }

  // Restore param modal from URL once parameters load (don't write URL — it's already there)
  useEffect(() => {
    if (!chartParamIdParam || chartParam) return;
    const match = parameters.find((p) => p.id === chartParamIdParam);
    if (match) setChartParam(match);
  }, [chartParamIdParam, parameters, chartParam]);

  const activeLayerId = searchParams.get('layer') || null;
  const setActiveLayerId = useCallback((id) => {
    setSearchParams((prev) => { const p = new URLSearchParams(prev); p.set('layer', id); return p; }, { replace: true });
    setParamSearch('');
  }, [setSearchParams, setParamSearch]);

  const { from, to } = useMemo(() => rangeToISO(rangeDays), [rangeDays]);
  const { data: liveData = [] } = useParameterLive(id);
  const { data: statsData = [] } = useParameterStats(id, from, to);

  // Index by parameterId for O(1) lookup in row render
  const liveByParamId = Object.fromEntries(liveData.map((d) => [d.parameterId, d]));
  const statsByParamId = Object.fromEntries(statsData.map((d) => [d.parameterId, d]));

  useEffect(() => {
    setActiveDeployment(id);
    return () => setActiveDeployment(null);
  }, [id, setActiveDeployment]);

  // Set default tab to first layer
  useEffect(() => {
    if (layers.length && !activeLayerId) {
      setActiveLayerId(layers[0]?.id ?? null);
    }
  }, [layers, activeLayerId, setActiveLayerId]);

  const isLoading = depLoading || layersLoading || paramsLoading;
  if (isLoading) return <SkeletonCard rows={5} height={300} />;
  if (depError || layersError || paramsError) {
    return <ErrorState message={depErr?.message ?? 'Failed to load dashboard data.'} />;
  }

  const sortedLayers = [...layers].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const activeLayer = sortedLayers.find((l) => l.id === activeLayerId) ?? sortedLayers[0];
  const layerParams = parameters.filter((p) =>
    activeLayer?.parameterIds?.includes(p.id)
  );

  function handleDownloadAll() {
    setDownloading(true);
    try {
      const headers = ['Parameter', 'Type', 'Unit', 'Layer', 'Latest Value', 'Status', 'Last Updated', 'Avg', 'Std Dev', `Off Days / ${rangeDays}`, 'Optimal', 'Data Key', 'Integration Key'];
      const rows = parameters.map((p) => {
        const layerName = sortedLayers.find((l) => l.parameterIds?.includes(p.id))?.name ?? '';
        const live = liveByParamId[p.id];
        const stats = statsByParamId[p.id];
        const liveVal = live?.value != null ? `${live.value} ${p.unit ?? ''}`.trim() : '';
        const lastUpdated = formatTimestamp(live?.timestamp ?? null);
        const state = live?.state;
        const statusMeta = live?.value != null
          ? (state ? (PARAM_STATUS_META[state] ?? PARAM_STATUS_META.just_off) : (isInOptimal(live.value, p) ? PARAM_STATUS_FALLBACK_OK : PARAM_STATUS_FALLBACK_ALERT))
          : null;
        const status = statusMeta?.label ?? '';
        const offDays = stats?.offDays != null ? `${stats.offDays}/${stats.totalDays ?? rangeDays}` : '';
        return [
          p.displayName ?? p.name ?? '',
          p.type ?? '',
          p.unit ?? '',
          layerName,
          liveVal,
          status,
          lastUpdated,
          stats?.avg != null ? stats.avg : '',
          stats?.stdDev != null ? stats.stdDev : '',
          offDays,
          formatOptimal(p),
          p.dataKey ?? '',
          p.integrationKey ?? '',
        ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',');
      });
      const BOM = '﻿';
      const csv = BOM + [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `parameters-all-layers.csv`; a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }
  const filteredParams = paramSearch.trim()
    ? layerParams.filter((p) => (p.displayName ?? p.name ?? '').toLowerCase().includes(paramSearch.toLowerCase()))
    : layerParams;


  return (
    <div style={{ fontFamily: typography.fontFamily.body, width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="All Parameters"
        description={`${deployment?.name ?? ''} · Parameter summary`}
        actions={
          <div style={{ display: 'inline-flex', borderRadius: radius.lg, border: `1px solid ${c.border}`, background: isDark ? 'rgba(255,255,255,0.04)' : c.bgSubtle, padding: '3px', gap: '2px' }}>
            {DATE_RANGES.map((r) => {
              const active = rangeDays === r.days;
              return (
                <button
                  key={r.days}
                  onClick={() => setRangeDays(r.days)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: radius.md,
                    border: 'none',
                    background: active ? (isDark ? 'rgba(255,255,255,0.1)' : '#fff') : 'transparent',
                    color: active ? c.textActive : c.textSubtle,
                    fontSize: typography.fontSize.md,
                    fontWeight: active ? 600 : 500,
                    fontFamily: typography.fontFamily.body,
                    cursor: 'pointer',
                    boxShadow: active ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                    transition: 'all 0.15s',
                  }}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
        }
      />

      {/* Empty state — no layers */}
      {sortedLayers.length === 0 && (
        <div style={{
          background: c.bg, border: `1px solid ${c.border}`,
          borderRadius: radius['3xl'], padding: '80px 40px', textAlign: 'center',
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 40, color: c.textSubtle, display: 'block', marginBottom: '12px' }}>
            layers
          </span>
          <h2 style={{ margin: '0 0 8px', fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontFamily: typography.fontFamily.heading }}>
            No layers configured
          </h2>
          <p style={{ margin: 0, fontSize: typography.fontSize.md, color: c.textSubtle }}>
            Add layers and assign parameters in Settings to see the dashboard.
          </p>
        </div>
      )}

      {/* Layer tabs + table */}
      {sortedLayers.length > 0 && (
        <div style={{
          flex: 1,
          minHeight: 0,
          background: c.bg,
          border: `1px solid ${c.border}`,
          borderRadius: radius['2xl'],
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: isDark ? 'none' : '0 2px 8px rgba(30,67,150,0.05)',
        }}>
          {/* Tabs row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: `1px solid ${c.border}`,
            background: c.bgSubtle,
            padding: '0 4px',
            overflowX: 'auto',
          }}>
            <span style={{
              fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle,
              letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase',
              padding: '0 12px', whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              PARAMETER SUMMARY
            </span>
            <div style={{ width: '1px', height: '20px', background: c.border, flexShrink: 0 }} />
            {sortedLayers.map((layer) => {
              const active = layer.id === activeLayer?.id;
              const paramCount = parameters.filter((p) => layer.parameterIds?.includes(p.id)).length;
              return (
                <button
                  key={layer.id}
                  onClick={() => setActiveLayerId(layer.id)}
                  style={{
                    padding: '12px 16px',
                    border: 'none',
                    borderBottom: active ? '2px solid #1e4396' : '2px solid transparent',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: typography.fontSize.base,
                    fontWeight: active ? 700 : 500,
                    color: active ? c.textActive : c.textSubtle,
                    fontFamily: typography.fontFamily.body,
                    whiteSpace: 'nowrap',
                    transition: 'all 0.15s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexShrink: 0,
                  }}
                >
                  {layer.name}
                  <span style={{
                    fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
                    padding: '1px 6px', borderRadius: radius.lg,
                    background: active ? (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(30,67,150,0.1)') : (isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'),
                    color: active ? c.textActive : c.textSubtle,
                  }}>
                    {paramCount}
                  </span>
                </button>
              );
            })}

            {/* Search — right-aligned, pushed to end */}
            <SearchInput
              value={paramSearch}
              onChange={setParamSearch}
              placeholder="Search parameter…"
              size="sm"
              containerStyle={{ marginLeft: 'auto', flex: '0 1 260px', minWidth: 180 }}
            />
          </div>

          {/* Parameter table */}
          {layerParams.length === 0 ? (
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 32, color: c.textSubtle, display: 'block', marginBottom: '10px' }}>
                tune
              </span>
              <p style={{ margin: 0, fontSize: typography.fontSize.md, color: c.textSubtle }}>
                No parameters assigned to this layer. Go to Settings → Layers to assign.
              </p>
            </div>
          ) : filteredParams.length === 0 ? (
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 32, color: c.textSubtle, display: 'block', marginBottom: '10px' }}>
                search_off
              </span>
              <p style={{ margin: 0, fontSize: typography.fontSize.md, color: c.textSubtle }}>No parameters match &quot;<strong>{paramSearch}</strong>&quot;</p>
            </div>
          ) : (
            <div style={{ flex: 1, minHeight: 0 }}>
              <ParameterTable
                rows={filteredParams}
                liveByParamId={liveByParamId}
                statsByParamId={statsByParamId}
                rangeDays={rangeDays}
                isDark={isDark}
                onRowClick={openParam}
              />
            </div>
          )}

          {/* Footer */}
          <div style={{
            padding: '8px 20px',
            borderTop: `1px solid ${c.border}`,
            background: c.bgSubtle,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 12, color: c.textSubtle }}>info</span>
            <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle, flex: 1 }}>
              Live values refresh every 60s · Avg and σ computed over last {rangeDays} day{rangeDays !== 1 ? 's' : ''} · Off days = days outside optimal range
            </span>
            <button
              onClick={handleDownloadAll}
              disabled={downloading || parameters.length === 0}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                border: 'none', background: 'none', cursor: 'pointer', padding: '2px 4px',
                fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: c.textActive,
                fontFamily: typography.fontFamily.body, flexShrink: 0,
                opacity: (downloading || parameters.length === 0) ? 0.5 : 1,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 13 }}>download</span>
              Download All
            </button>
          </div>
        </div>
      )}

      {chartParam && (
        <ParameterChartsModal
          depId={id}
          param={chartParam}
          allParams={parameters}
          initialRangeDays={rangeDays}
          onClose={closeParam}
        />
      )}
    </div>
  );
}

// ─── Parameter table ──────────────────────────────────────────────────────────
function ParameterTable({ rows, liveByParamId, statsByParamId, rangeDays, isDark: _isDark, onRowClick }) {
  const c = useThemeColors();
  const columns = useMemo(() => [
    {
      field: 'displayName',
      headerName: 'Parameter',
      flex: 1.6,
      minWidth: 180,
      renderCell: ({ row }) => (
        <div>
          <div style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold }}>{row.displayName}</div>
          <div style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, marginTop: '1px', letterSpacing: typography.letterSpacing.wide }}>
            {row.type?.toUpperCase()}
          </div>
        </div>
      ),
    },
    {
      field: '__live',
      headerName: 'Latest',
      flex: 1,
      minWidth: 130,
      sortable: false,
      valueGetter: (_, row) => liveByParamId[row.id]?.value ?? -Infinity,
      renderCell: ({ row }) => {
        const live = liveByParamId[row.id];
        const val = live?.value ?? null;
        if (val === null) return <span style={{ color: c.textSubtle }}>— {row.unit}</span>;
        const ok = isInOptimal(val, row);
        return (
          <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold, color: ok ? semantic.successAlt : semantic.errorAlt }}>
            {val} <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>{row.unit}</span>
          </span>
        );
      },
    },
    {
      field: '__updatedAt',
      headerName: 'Last Updated',
      flex: 1.2,
      minWidth: 150,
      sortable: true,
      valueGetter: (_, row) => liveByParamId[row.id]?.timestamp ?? '',
      renderCell: ({ row }) => {
        const live = liveByParamId[row.id];
        const ts = live?.timestamp ?? null;
        const relative = timeAgo(ts);
        const exact = formatTimestamp(ts);
        if (!relative) return <span style={{ color: c.textSubtle }}>—</span>;
        return (
          <div>
            <div style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold }}>{relative}</div>
            <div style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, marginTop: '2px' }}>{exact}</div>
          </div>
        );
      },
    },
    {
      field: '__avg',
      headerName: 'Avg',
      flex: 0.7,
      minWidth: 80,
      sortable: false,
      valueGetter: (_, row) => statsByParamId[row.id]?.avg ?? -Infinity,
      renderCell: ({ row }) => {
        const s = statsByParamId[row.id];
        return <span>{s?.avg ?? '—'}</span>;
      },
    },
    {
      field: '__optimal',
      headerName: 'Optimal',
      flex: 0.9,
      minWidth: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <span style={{ color: c.textSubtle }}>{formatOptimal(row)}</span>
      ),
    },
    {
      field: '__sigma',
      headerName: 'Std Dev',
      flex: 0.6,
      minWidth: 70,
      sortable: false,
      valueGetter: (_, row) => statsByParamId[row.id]?.stdDev ?? -Infinity,
      renderCell: ({ row }) => {
        const s = statsByParamId[row.id];
        return <span>{s?.stdDev ?? '—'}</span>;
      },
    },
    {
      field: '__offdays',
      headerName: `Off Days / ${rangeDays}`,
      flex: 0.9,
      minWidth: 110,
      sortable: true,
      valueGetter: (_, row) => statsByParamId[row.id]?.offDays ?? -Infinity,
      renderCell: ({ row }) => {
        const s = statsByParamId[row.id];
        const offDays = s?.offDays ?? null;
        const totalDays = s?.totalDays ?? rangeDays;
        if (offDays === null) return <span style={{ color: c.textSubtle }}>—</span>;
        const ratio = offDays / totalDays;
        const color = ratio === 0 ? semantic.successAlt : ratio >= 0.5 ? semantic.errorAlt : semantic.warning;
        return <span style={{ fontWeight: offDays > 0 ? 700 : 400, color }}>{offDays}/{totalDays}</span>;
      },
    },
    {
      field: '__status',
      headerName: 'Status',
      flex: 0.7,
      minWidth: 90,
      sortable: true,
      valueGetter: (_, row) => {
        const live = liveByParamId[row.id];
        if (!live || live.value == null) return -1;
        const state = live.state;
        if (!state) return isInOptimal(live.value, row) ? 1 : 0;
        if (state === 'ok') return 1;
        if (state === 'stale') return -1;
        return 0; // just_off, predictive, ongoing → alert
      },
      renderCell: ({ row }) => {
        const live = liveByParamId[row.id];
        if (!live || live.value == null) return <span style={{ fontSize: typography.fontSize.base, color: c.textSubtle }}>No data</span>;
        const state = live.state;
        const meta = state
          ? (PARAM_STATUS_META[state] ?? PARAM_STATUS_META.just_off)
          : (isInOptimal(live.value, row) ? PARAM_STATUS_FALLBACK_OK : PARAM_STATUS_FALLBACK_ALERT);
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: radius.full, background: meta.bg, border: `1px solid ${meta.border}`, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold, color: meta.color }}>
            <span className="material-symbols-outlined" style={{ fontSize: 12, fontVariationSettings: "'FILL' 1" }}>{meta.icon}</span>{meta.label}
          </span>
        );
      },
    },
    {
      field: '__charts',
      headerName: '',
      width: 110,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: () => (
        <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: c.textActive, fontFamily: typography.fontFamily.body }}>
          View Trends
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
        </span>
      ),
    },
  ], [liveByParamId, statsByParamId, rangeDays, c]);

  return <AppTable rows={rows} columns={columns} getRowId={(r) => r.id} rowHeight={68} getRowHeight={() => 'auto'} hideFooter initialState={{ pagination: { paginationModel: { pageSize: 1000 } } }} onRowClick={({ row }) => onRowClick?.(row)} />;
}

